import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  CreditCard,
  Gauge,
  LogOut,
  Moon,
  Sun,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import type { User } from 'firebase/auth';
import type { OrgRole } from '@leanopx/shared';
import { useAuth, useIsOrgAdmin } from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { initialsFor } from '@/lib/initials';

export function ProfileMenu() {
  const navigate = useNavigate();
  const { user, activeOrg, signOutUser } = useAuth();
  const isOrgAdmin = useIsOrgAdmin();
  const role: OrgRole | null = activeOrg?.role ?? null;
  const { theme, density, toggleTheme, cycleDensity } = useUiStore();

  if (!user) return null;

  const displayName = user.displayName?.trim() || user.email || '';
  const initials = getInitials(user);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('menu.account')}
          title={displayName}
          className={cn(
            'relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-pill border border-[var(--color-border)] bg-[var(--color-elevated)] text-xs font-medium text-[var(--color-ink)] transition-colors',
            'hover:border-[var(--color-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-0',
            'data-[state=open]:border-[var(--color-accent)] data-[state=open]:ring-2 data-[state=open]:ring-[var(--color-accent)]',
          )}
        >
          <Avatar user={user} initials={initials} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          align="end"
          className="z-50 min-w-[240px] rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm shadow-md"
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-pill border border-[var(--color-border)] bg-[var(--color-elevated)] text-xs font-medium text-[var(--color-ink)]">
              <Avatar user={user} initials={initials} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-[var(--color-ink)]">{displayName}</div>
              {user.email && user.email !== displayName && (
                <div className="truncate font-mono text-xxs text-[var(--color-ink-muted)]">
                  {user.email}
                </div>
              )}
              {role && <RoleBadge role={role} />}
            </div>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          {/* v6 dev-handoff (#114): nav-only entries to the new
              Profile + Billing surfaces. Both pages are UI shells today
              — Profile reads live `auth.currentUser` for identity but
              the channels/rhythm rails are placeholders pending
              `userPreferences/{uid}`; Billing renders a Stripe-shaped
              shell with no live subscription wiring. We surface a
              "preview" pill so users/stakeholders aren't surprised by
              the no-op CTAs, and Billing stays admin-gated to match the
              route guard. */}
          <DropdownMenu.Item
            onSelect={() => navigate({ to: '/profile' })}
            className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
          >
            <UserIcon className="size-4" aria-hidden />
            <span className="flex-1">{t('profile.eyebrow')}</span>
            <PreviewPill />
          </DropdownMenu.Item>
          {isOrgAdmin && (
            <DropdownMenu.Item
              onSelect={() => navigate({ to: '/admin/billing' })}
              className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
            >
              <CreditCard className="size-4" aria-hidden />
              <span className="flex-1">{t('billing.eyebrow')}</span>
              <PreviewPill />
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          <MenuRow icon={Gauge} label={t('menu.density')} value={density} onSelect={cycleDensity} />
          <MenuRow
            icon={theme === 'midnight' ? Sun : Moon}
            label={t('menu.theme')}
            value={t(`theme.${theme}`)}
            onSelect={() => toggleTheme()}
          />

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          <DropdownMenu.Item
            onSelect={() => signOutUser()}
            className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
          >
            <LogOut className="size-4 icon-directional" aria-hidden />
            <span>{t('auth.signout')}</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function MenuRow({
  icon: Icon,
  label,
  value,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        // Keep menu open so users can cycle through values without reopening.
        e.preventDefault();
        onSelect();
      }}
      className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
    >
      <Icon className="size-4" aria-hidden />
      <span className="flex-1">{label}</span>
      <span className="font-mono text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
        {value}
      </span>
    </DropdownMenu.Item>
  );
}

/**
 * Small "preview" hint pill rendered next to menu entries pointing at
 * UI shells (Profile, Billing) — flags that the destination is still in
 * dev-handoff and that some affordances are no-ops. Removed in the same
 * follow-up that wires the backend surfaces (#114).
 */
function PreviewPill() {
  return (
    <span
      className="rounded-xs bg-[var(--color-elevated)] px-1.5 py-0.5 font-mono text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]"
      title={t('menu.preview.title')}
    >
      {t('menu.preview')}
    </span>
  );
}

function RoleBadge({ role }: { role: OrgRole }) {
  return (
    <span className="mt-0.5 inline-block rounded-xs bg-[var(--color-accent-soft)] px-1 py-0.5 font-mono text-xxs uppercase tracking-wider text-[var(--color-accent)]">
      {role}
    </span>
  );
}

function Avatar({ user, initials }: { user: User; initials: string }) {
  const [errored, setErrored] = useState(false);
  if (user.photoURL && !errored) {
    return (
      <img
        src={user.photoURL}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
        className="size-full object-cover"
      />
    );
  }
  return <span aria-hidden>{initials}</span>;
}

function getInitials(user: User): string {
  // `||` (not `??`) so an empty-string `displayName` falls through to
  // email — `??` only falls through on null/undefined and would render
  // a blank avatar for users with `displayName === ""`.
  return initialsFor(user.displayName || user.email);
}
